import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useEffect, useMemo } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { findFirstNonEmptyString } from "scmp-sdk";

import { section as sectionData } from "shared/data";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { useTopBannerAdSlot } from "scmp-app/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { ContentShareWidgetDrawer } from "scmp-app/components/content/content-share-widget-drawer";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PageSeo } from "scmp-app/components/meta";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type { videoPageQuery } from "scmp-app/queries/__generated__/videoPageQuery.graphql";

import {
  Container,
  Header,
  OneLineMenuContainer,
  SectionName,
  SectionNameContainer,
  StyledEntityOnelineMenuLite,
  StyledVideoHeaderDropdownMenu,
  StyledVideoLatestVideo,
  StyledVideoList,
  StyledVideoShortCarousel,
} from "./styles";
import VideoSeoImage from "./video-seo-image.png";

const query = graphql`
  query videoPageQuery(
    $after: String
    $count: Int = 24
    $entityId: String!
    $sectionIds: [String]
    $videoLatestQueueName: String!
    $videoShortQueueName: String!
  ) {
    section(filter: { entityId: $entityId }) {
      advertZone(version: 2)
    }
    oneLineMenu: queue(filter: { name: "video_highlight_menu" }) {
      items(first: 20) {
        edges {
          ...entityOnelineMenuLiteQueueItemsEdge
        }
      }
    }
    ...videoHeaderDropdownMenuQuery @arguments(withHighlight: false)
    ...latestVideoQuery @arguments(videoLatestQueueName: $videoLatestQueueName)
    ...videoListQuery @arguments(after: $after, count: $count, sectionIds: $sectionIds)
    ...videoShortCarousel @arguments(queueName: $videoShortQueueName)
  }
`;

const VideoPage: RelayComponent<PageProps, videoPageQuery> = ({ preloadedQuery }) => {
  const queryReference = usePreloadedQuery(query, preloadedQuery);
  const adZone = useMemo(
    () => findFirstNonEmptyString(queryReference.section?.advertZone) ?? "default",
    [queryReference.section?.advertZone],
  );
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  useTopBannerAdSlot({
    desktop: {
      adUnit: "d_banner1",
      sizes: [
        [970, 250],
        [728, 90],
        [970, 90],
      ],
      targeting: {
        paid: TargetingPaidType.NotArticle,
      },
      zone: adZone,
    },
    mobile: {
      adUnit: "m_banner3",
      sizes: [
        [300, 100],
        [320, 100],
        [300, 50],
        [320, 50],
      ],
      targeting: {
        paid: TargetingPaidType.NotArticle,
      },
      zone: adZone,
    },
  });

  return (
    <>
      <PageSeo openGraph={{ images: [{ url: VideoSeoImage.src }] }} title="Video" />
      <ContentShareWidgetDrawer />
      <Container>
        <Header>
          <SectionNameContainer>
            <SectionName>VIDEO</SectionName>
          </SectionNameContainer>
          <OneLineMenuContainer>
            <StyledEntityOnelineMenuLite
              reference={queryReference.oneLineMenu?.items?.edges ?? []}
            />
            <StyledVideoHeaderDropdownMenu reference={queryReference} withCloseButton={isMobile} />
          </OneLineMenuContainer>
        </Header>
        <StyledVideoLatestVideo
          adZone={adZone}
          ga4Position="video_home"
          reference={queryReference}
        />
        <StyledVideoShortCarousel reference={queryReference} />
        <StyledVideoList data={queryReference} />
      </Container>
    </>
  );
};

VideoPage.displayName = "VideoPage";

const pageProps: PageProps = {
  appBarConfiguration: {
    adSlotVariants: {
      large: {
        height: 250,
        padding: 20,
      },
      small: {
        height: 100,
        padding: 10,
      },
    },
    hasDesktopAd: true,
    hasMobileAd: true,
    variant: "scmp/slim",
  },
  contentConfiguration: {
    responsivePadding: {
      desktopUp: 12,
      tabletUp: 0,
    },
  },
  pageConfiguration: {
    responsivePadding: {
      tabletUp: 32,
    },
  },
};

export default withRelay(VideoPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext() {
    return {
      entityId: sectionData.video.entityId,
      videoLatestQueueName: "video_homepage_top",
      videoShortQueueName: "homepage_video_shorts",
    };
  },
});
